import { differenceInDays, format, startOfDay } from 'date-fns';
import { Omit } from 'lodash';

// ====================== Task

export interface TaskBase {
  /**
   * Id of the task.
   * - If this is a serial task or real single task, the this is the normal id.
   * - If this is a single task in a series, this is the id of the serial task
   *   concatenated with the timestamp of the original due date.
   */
  id: string;
  /**
   * Schenck task id.
   */
  schenckId?: string;
  /**
   * Customer task id.
   */
  customerId?: string;
  /**
   * Task title.
   */
  title: string;
  /**
   * Priority of the task.
   */
  priority: Priority;
  /**
   * Pictures for the task.
   */
  pictureIds?: string[];
  /**
   * Additional notice on the task.
   */
  notice?: string;
  /**
   * Additional notes on the task.
   */
  notes?: string;
  /**
   * A short description of the task.
   */
  description?: string;
  /**
   * What kind of task this is.
   */
  type?: TaskType;
  /**
   * How long this task takes
   */
  expectedDurationMinutes?: number;
  /**
   * The company that is responsible for this task.
   */
  responsibleCompany: ResponsibleCompany;
  /**
   * A person that is responsible for this task.
   */
  responsible?: string;
  /**
   * A person that should execute this task.
   */
  executive?: string;
  /**
   * References to documents (not the documents itself)
   */
  documentIds?: string[];
  /**
   * References to documents only relevant for this very single task.
   */
  taskDocumentIds?: string[];
  /**
   * Actual date of task execution (set through done).
   */
  executionDate?: Date;
  /**
   * Date when task is due. Can be shifted.
   */
  dueDate: Date;
  /**
   * Used under the hood to get reference back to serial task.
   * Shall not be modified after creation.
   */
  originalDueDate: Date;
  /**
   * Used to calculate the status.
   */
  referenceDate: Date;
  /**
   * Percentage of how much until due date.
   * 0 = far way, 100 = due/overdue.
   * Calculated in frontend.
   */
  status: number;
  /**
   * If this is a serial task, this is set.
   */
  scheduling: Scheduling;
  /**
   * If certain user's should be notified, this is set.
   */
  alarm: AlarmModel;
  /**
   * If task was finished, the reason.
   */
  finishInfo?: FinishInfo;
  /**
   * If task was skipped, the reason.
   */
  skipInfo?: SkipInfo;
  /**
   * If task was shifted, the entries.
   */
  shiftings?: Shifting[];
  /**
   * Info about the series, the task might belong to.
   */
  serialTask?: SerialTask;
}

export interface FinishInfo {
  /**
   * Reason why task is finished.
   */
  finishReason: string;
  /**
   * Date at which the action was done.
   */
  timeStamp: Date;
}

export interface SkipInfo {
  /**
   * Reason why task is skipped.
   */
  skipReason: string;
  /**
   * Date at which the action was done.
   */
  timeStamp: Date;
}

export interface Shifting {
  /**
   * Due date before shift.
   */
  previousDueDate: Date;
  /**
   * Due date after shift.
   */
  shiftedDueDate: Date;
  /**
   * Reason for shifting.
   */
  shiftReason: string;
  /**
   * Date at which the action was done.
   */
  timeStamp: Date;
}

export interface SerialTask {
  /**
   * If the series is still active or has been deleted.
   */
  isActive: boolean;
  /**
   * Id of the series, the task might belong to
   */
  serialId: string;
}

export enum Priority {
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
}

export enum TaskType {
  Electrical = 'Electrical',
  Mechanical = 'Mechanical',
  IT = 'IT',
  Other = 'Other',
}

export enum ResponsibleCompany {
  Schenck = 'Schenck',
  Customer = 'Customer',
}

// ====================== Alarm

export interface AlarmModel {
  offsetUnit: AlarmOffsetUnit;
  offsetValue: number;
  additionalRecipients: string[];
}

export enum AlarmOffsetUnit {
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
}

// ====================== Scheduling

export enum Month {
  January = 1,
  February = 2,
  March = 3,
  April = 4,
  May = 5,
  June = 6,
  July = 7,
  August = 8,
  September = 9,
  October = 10,
  November = 11,
  December = 12,
}

export enum TaskIntervalPattern {
  NotSet = 'NotSet',
  Daily = 'Daily',
  Weekly = 'Weekly',
  Monthly = 'Monthly',
  Yearly = 'Yearly',
  EveryWorkDay = 'EveryWorkDay',
  MonthlyByWeekDayAppearance = 'MonthlyByWeekDayAppearance',
  YearlyByWeekDayAppearance = 'YearlyByWeekDayAppearance',
}

export enum DayOfWeek {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
}

export enum DayOfWeekAppearanceInMonth {
  First = 'First',
  Second = 'Second',
  Third = 'Third',
  Fourth = 'Fourth',
  Last = 'Last',
}

export interface Scheduling {
  appearance: DayOfWeekAppearanceInMonth;
  dayOfMonth: number;
  endDate: Date;
  interval: number;
  month: Month;
  pattern: TaskIntervalPattern;
  startDate: Date;
  weekDays: DayOfWeek[];
}

// ====================== FrontModel <-> BackendModel Mapper

export class TaskMapper {
  /**
   * Maps frontend model to backend model.
   * Mainly this stringifies the dates.
   */
  static mapToBackend<T extends TaskBase>(task: Partial<T>): any {
    return {
      ...task,
      dueDate: TaskMapper.stringifyDate(task.dueDate),
      originalDueDate: TaskMapper.stringifyDate(task.originalDueDate),
      referenceDate: TaskMapper.stringifyDate(task.referenceDate),
      executionDate: TaskMapper.stringifyDate(task.executionDate),
      scheduling: TaskMapper.stringifyScheduling(task.scheduling),
      // ignored by backend, so don't send them:
      shiftings: undefined,
      finishReason: undefined,
      skipReason: undefined,
    };
  }

  static stringifyDate(date: Date | null): string | null {
    return date ? format(date, `yyyy-MM-dd'T'HH:mm:ss`) : null;
  }

  private static stringifyScheduling(scheduling: any): Scheduling | null {
    if (!scheduling) {
      return null;
    }
    return {
      ...scheduling,
      startDate: TaskMapper.stringifyDate(scheduling.startDate),
      endDate: TaskMapper.stringifyDate(scheduling.endDate),
    };
  }

  /**
   * Maps raw backend model to the frontend model.
   * Mainly this parses the dates and adds status.
   */
  static mapFromBackend<T extends TaskBase>(raw: any): T | null {
    if (!raw) {
      return null;
    }

    const taskBase: Omit<TaskBase, 'status'> = {
      ...raw,
      dueDate: TaskMapper.parseDate(raw.dueDate),
      originalDueDate: TaskMapper.parseDate(raw.originalDueDate),
      referenceDate: TaskMapper.parseDate(raw.referenceDate),
      executionDate: TaskMapper.parseDate(raw.executionDate),
      scheduling: TaskMapper.parseScheduling(raw.scheduling),
      shiftings: (raw.shiftings || []).map((shifting) => ({
        ...shifting,
        previousDueDate: TaskMapper.parseDate(shifting.previousDueDate),
        shiftedDueDate: TaskMapper.parseDate(shifting.shiftedDueDate),
        timeStamp: TaskMapper.parseDate(shifting.timeStamp),
      })),
      finishInfo: raw.finishInfo && {
        ...raw.finishInfo,
        timeStamp: TaskMapper.parseDate(raw.finishInfo.timeStamp),
      },
      skipInfo: raw.skipInfo && {
        ...raw.skipInfo,
        timeStamp: TaskMapper.parseDate(raw.skipInfo.timeStamp),
      },
    };
    return {
      ...(<any>taskBase),
      status: TaskMapper.calculateStatus(taskBase.referenceDate, taskBase.dueDate),
    };
  }

  private static parseScheduling(scheduling: any): Scheduling | null {
    if (!scheduling) {
      return null;
    }
    return {
      ...scheduling,
      startDate: TaskMapper.parseDate(scheduling.startDate),
      endDate: TaskMapper.parseDate(scheduling.endDate),
    };
  }

  static parseDate(dateStr: string): Date | null {
    return dateStr ? new Date(dateStr) : null;
  }

  private static calculateStatus(referenceDate: Date, dueDate: Date): number {
    const timespan = differenceInDays(startOfDay(dueDate), startOfDay(referenceDate)) || 1;
    const daysLeft = differenceInDays(startOfDay(dueDate), startOfDay(new Date()));

    let progress: number;

    if (daysLeft < 0) {
      progress = 0;
    } else if (daysLeft >= 0) {
      if (daysLeft > timespan) {
        progress = 100;
      } else {
        progress = (daysLeft * 100) / timespan;
      }
    }
    return Math.round(progress);
  }
}
